import {
  Body,
  Controller,
  Delete,
  Get,
  Param,
  Patch,
  Post,
  Query,
  UseGuards,
} from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { RequireModule } from '../../auth/decorators/require-module.decorator';
import { RequirePermission } from '../../auth/decorators/require-permission.decorator';
import type { LoginUserInfo } from '../../auth/dto/login.dto';
import { GetUser } from '../../auth/get-user.decorator';
import { ModuleGuard } from '../../auth/guards/module.guard';
import { PermissionGuard } from '../../auth/guards/permission.guard';
import {
  CreateTipoDocumentoDto,
  QueryTiposDocumentoDto,
  UpdateTipoDocumentoDto,
} from '../dto/legajos.dto';
import { LegajosTiposService } from '../services/legajos-tipos.service';

@ApiTags('RRHH - Legajos - Catálogo de tipos (M23)')
@Controller({ path: 'rrhh/legajos/tipos', version: '1' })
@UseGuards(AuthGuard('jwt'), ModuleGuard, PermissionGuard)
@RequireModule('RRHH')
@ApiBearerAuth()
export class LegajosTiposController {
  constructor(private readonly service: LegajosTiposService) {}

  @Get()
  @RequirePermission('RRHH', 'RH_CFG_CATALOGO_VER')
  @ApiOperation({
    summary: 'Listar tipos de documento (sistema + personalizados de la empresa)',
  })
  listar(@GetUser() user: LoginUserInfo, @Query() query: QueryTiposDocumentoDto) {
    return this.service.listar(user.empresa_id, query);
  }

  @Get(':id')
  @RequirePermission('RRHH', 'RH_CFG_CATALOGO_VER')
  @ApiOperation({ summary: 'Obtener tipo de documento por ID' })
  obtener(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.obtener(id, user.empresa_id);
  }

  @Post()
  @RequirePermission('RRHH', 'RH_CFG_CATALOGO_EDITAR')
  @ApiOperation({ summary: 'Crear tipo de documento personalizado' })
  crear(@GetUser() user: LoginUserInfo, @Body() dto: CreateTipoDocumentoDto) {
    return this.service.crear(user.empresa_id, dto, user.id);
  }

  @Patch(':id')
  @RequirePermission('RRHH', 'RH_CFG_CATALOGO_EDITAR')
  @ApiOperation({
    summary: 'Actualizar tipo de documento personalizado (no aplica a tipos del sistema)',
  })
  actualizar(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: UpdateTipoDocumentoDto,
  ) {
    return this.service.actualizar(id, user.empresa_id, dto);
  }

  @Delete(':id')
  @RequirePermission('RRHH', 'RH_CFG_CATALOGO_EDITAR')
  @ApiOperation({
    summary: 'Desactivar tipo de documento personalizado (soft delete — no aplica a tipos del sistema)',
  })
  desactivar(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.desactivar(id, user.empresa_id);
  }
}
